import {
  File as FileIcon,
  FileText,
  FileType,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileSpreadsheet,
  FileCode,
  FileJson,
  Presentation,
} from 'lucide-react';
import type { ElementType } from 'react';
import { extensionOf } from '../services/blockedExtensionService';

/**
 * 파일 이름 → 확장자에 맞는 아이콘.
 *
 * 판정은 여기 하나뿐이고, **색은 호출부가 정한다** — 말풍선은 brand-600(다크 표면은 gray-300),
 * 서랍·입력창 미리보기는 slate-400처럼 화면마다 톤이 다르기 때문이다.
 * 타입별 관례색(PDF 빨강 등)을 쓰지 않는 이유는 CLAUDE.md의 "brand-*가 액션·경고·에러의 모든 레드를
 * 소유한다" 규칙 — 파일 아이콘이 빨개지면 경고로 읽힌다.
 *
 * ⚠ 만료된 첨부는 이 함수를 거치지 않고 `Clock`을 그린다(만료 신호가 파일 타입보다 우선).
 */

/** 확장자(앞의 점 제외) → 아이콘. 없는 확장자는 일반 파일 아이콘 */
const ICON_BY_EXTENSION: Record<string, ElementType> = {};

const register = (icon: ElementType, extensions: string[]) => {
  extensions.forEach(ext => { ICON_BY_EXTENSION[ext] = icon; });
};

register(FileImage, ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tif', 'tiff', 'ico', 'avif']);
register(FileVideo, ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'm4v', 'flv', 'mpg', 'mpeg']);
register(FileAudio, ['mp3', 'wav', 'm4a', 'flac', 'ogg', 'aac', 'wma', 'opus']);
register(FileArchive, ['zip', '7z', 'rar', 'tar', 'gz', 'bz2', 'xz', 'alz', 'egg']);
register(FileSpreadsheet, ['xls', 'xlsx', 'xlsm', 'xlsb', 'csv', 'ods']);
register(Presentation, ['ppt', 'pptx', 'pps', 'ppsx', 'odp']);
register(FileType, ['doc', 'docx', 'hwp', 'hwpx', 'hml', 'rtf', 'odt', 'txt', 'md']);
register(FileText, ['pdf']);
register(FileJson, ['json']);
register(FileCode, [
  'js', 'mjs', 'cjs', 'ts', 'tsx', 'jsx', 'py', 'java', 'c', 'h', 'cpp', 'cs', 'go', 'rb', 'php',
  'rs', 'kt', 'swift', 'sql', 'sh', 'bat', 'ps1', 'html', 'htm', 'css', 'scss', 'xml', 'yml', 'yaml', 'ini',
]);

/**
 * 첨부 아이콘 컴포넌트를 고른다. 확장자를 모르면 일반 파일 아이콘.
 * 확장자 추출은 차단 목록 판정과 같은 `extensionOf()`를 재사용한다(점 포함 소문자 반환).
 */
export function iconForFileName(fileName: string): ElementType {
  const ext = extensionOf(fileName).replace(/^\./, '');
  return ICON_BY_EXTENSION[ext] ?? FileIcon;
}
